import axios from "axios";

export const createEstimate = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/estimates`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updateEstimate = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/estimates/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/**
 * Instant Estimate settings: the company minimum quantity in yards
 * (null = none). Readable by clients (the form shows it before submit);
 * writable by company users only.
 */
export const getEstimateSettings = async (): Promise<{ minQuantityYds: number | null }> => {
	try {
		const res: any = await axios.get(`/estimates/settings`, { excludeInterceptor: true } as any);
		return { minQuantityYds: res?.data?.minQuantityYds ?? null };
	} catch {
		return { minQuantityYds: null };
	}
};

/** Minimum for this client + site + product (company/client min, raised by depot minimums). */
export const getEstimateMinimum = async (q: { clientId?: string | null; latitude?: number | null; longitude?: number | null; productId?: string | null }): Promise<number | null | undefined> => {
	try {
		const params: any = {};
		if (q.clientId) params.clientId = q.clientId;
		if (q.latitude != null && q.longitude != null) { params.latitude = q.latitude; params.longitude = q.longitude; }
		if (q.productId) params.productId = q.productId;
		const res: any = await axios.get(`/estimates/minimum`, { params, excludeInterceptor: true } as any);
		return res?.data?.minQuantityYds ?? null;
	} catch {
		return undefined; // unknown - keep what the form already shows
	}
};

export const updateEstimateSettings = async (input: { minQuantityYds: number | null }) => {
	try {
		const res: any = await axios.put(`/estimates/settings`, input);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? { error: e.response.data.message ?? "Saving failed." } : { error: "Saving failed." };
	}
};

/**
 * Provider / depot minimums (Company Settings > Instant Estimate).
 * A depot whose minimum is above the order quantity drops out of the
 * estimate; depotId null = the provider-wide minimum.
 */
export interface DepotMinimumRow {
	id: string;
	providerId: string;
	providerName: string;
	depotId: string | null;
	depotName: string | null;
	/** null = all services */
	serviceId?: string | null;
	serviceName?: string | null;
	minQuantityYds: string;
}

export const getDepotMinimums = async (): Promise<DepotMinimumRow[] | { error: string }> => {
	try {
		const res: any = await axios.get(`/estimates/settings/depot-minimums`, { excludeInterceptor: true } as any);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return { error: e?.response?.data?.message ?? "Loading minimums failed." };
	}
};

export const setDepotMinimum = async (input: { providerId: string; depotId?: string | null; serviceId?: string | null; minQuantityYds: number | null }): Promise<DepotMinimumRow[] | { error: string }> => {
	try {
		const res: any = await axios.put(`/estimates/settings/depot-minimums`, input);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return { error: e?.response?.data?.message ?? "Saving the minimum failed." };
	}
};

/** Generate the quote for the chosen estimate service(s) - RFQ stays behind it. */
export const generateEstimateQuote = async (estimateId: string, selectServices: string[]) => {
	try {
		const res: any = await axios.post(`/estimates/${estimateId}/generate-quote`, { selectServices });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};